import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Bot, Globe, CheckCircle2, AlertCircle, RefreshCw, Server, Link2, FlaskConical, Users } from "lucide-react";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BotCard = ({ title, icon: Icon, info, expected, isCurrentEnv, accent }) => {
  const matches = info?.current_webhook === expected;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4" data-testid={`bot-card-${title.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-400 font-mono">
            {info?.username ? `@${info.username}` : <span className="text-red-500">Token mancante in .env</span>}
          </p>
        </div>
        {matches ? (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> Su questo ambiente
          </span>
        ) : (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200">
            <AlertCircle className="h-3.5 w-3.5" /> Su altro ambiente
          </span>
        )}
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">Webhook attualmente registrato</p>
          <code className="block mt-1 px-2 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-700 font-mono break-all">
            {info?.current_webhook || "(nessuno)"}
          </code>
          {(info?.pending_updates ?? 0) > 0 && (
            <p className="text-[11px] text-amber-600 mt-1">⚠ {info.pending_updates} update in coda</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">Webhook atteso per questo ambiente</p>
          <code className="block mt-1 px-2 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 font-mono break-all">
            {expected || "(REACT_APP_BACKEND_URL mancante)"}
          </code>
        </div>
      </div>
    </div>
  );
};

const TelegramCentralPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("sa_token");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/superadmin/telegram/central-status`, { headers });
      setStatus(res.data);
    } catch (e) {
      toast.error("Errore caricamento stato");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleClaim = async () => {
    if (!window.confirm(
      `Reclamare i webhook per questo ambiente (${status?.env?.backend_url})?\n\n` +
      `I bot smetteranno di rispondere su altri ambienti finché non clicchi di nuovo da lì.`
    )) return;

    setClaiming(true);
    try {
      const token = localStorage.getItem("sa_token");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/superadmin/telegram/claim-webhook`, {}, { headers });
      if (res.data.success) {
        toast.success("✓ Webhook reclamati con successo per questo ambiente");
      } else {
        const errs = Object.entries(res.data.results || {})
          .filter(([, v]) => !v.ok)
          .map(([k, v]) => `${k}: ${v.detail}`)
          .join(" · ");
        toast.error(`Errore parziale: ${errs}`);
      }
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore reclamo webhook");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  const isProduction = status?.env?.is_production;
  const allOnCurrentEnv = status?.central?.is_current_env && status?.demo?.is_current_env;

  return (
    <div className="space-y-5" data-testid="telegram-central-panel">
      <div>
        <h2 className="text-2xl font-black tracking-tight text-slate-900">Bot Telegram Centralizzati</h2>
        <p className="text-sm text-gray-500 mt-1">
          Architettura a 2 bot: <code className="px-1.5 py-0.5 rounded bg-gray-100 text-xs">@yollo_ai_bot</code> per tutti i clienti, <code className="px-1.5 py-0.5 rounded bg-gray-100 text-xs">@yollo_demo_bot</code> per l&apos;account demo.
        </p>
      </div>

      {/* Env banner */}
      <div className={`rounded-2xl p-4 border-2 ${isProduction ? "bg-red-50/40 border-red-200" : "bg-blue-50/40 border-blue-200"}`}>
        <div className="flex items-center gap-3">
          <Server className={`h-6 w-6 ${isProduction ? "text-red-500" : "text-blue-500"}`} />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Ambiente corrente</p>
            <p className="text-sm font-bold text-gray-900 break-all">
              {status?.env?.backend_url}
              {isProduction && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">PRODUZIONE</span>}
              {!isProduction && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">PREVIEW</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Claim button */}
      <div className={`rounded-2xl p-5 border-2 ${allOnCurrentEnv ? "bg-emerald-50/30 border-emerald-200" : "bg-amber-50/40 border-amber-300"}`}>
        <div className="flex items-start gap-4">
          {allOnCurrentEnv ? <CheckCircle2 className="h-7 w-7 text-emerald-500 flex-shrink-0" /> : <AlertCircle className="h-7 w-7 text-amber-500 flex-shrink-0" />}
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {allOnCurrentEnv
                ? "I bot ricevono i messaggi su questo ambiente"
                : "I bot stanno rispondendo su un altro ambiente"}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {allOnCurrentEnv
                ? "Tutto in regola. Se passi tra preview/produzione, ricorda di reclamare il webhook nel nuovo ambiente."
                : "Per ricevere e rispondere ai messaggi Telegram qui, devi reclamare il webhook. I messaggi smetteranno di arrivare sull'altro ambiente."}
            </p>
            <Button onClick={handleClaim} disabled={claiming}
              className={`rounded-xl text-white font-bold gap-2 ${isProduction ? "bg-red-500 hover:bg-red-600" : "bg-blue-500 hover:bg-blue-600"}`}
              data-testid="claim-webhook-btn">
              {claiming ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              Reclama webhook per questo ambiente
            </Button>
          </div>
        </div>
      </div>

      {/* Bot cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <BotCard title="Bot Centrale (clienti)" icon={Bot} info={status?.central}
          expected={status?.central?.expected_webhook}
          isCurrentEnv={status?.central?.is_current_env}
          accent="bg-gradient-to-br from-blue-500 to-cyan-600" />
        <BotCard title="Bot Demo" icon={FlaskConical} info={status?.demo}
          expected={status?.demo?.expected_webhook}
          isCurrentEnv={status?.demo?.is_current_env}
          accent="bg-gradient-to-br from-amber-500 to-orange-600" />
      </div>

      {/* Stats */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Connessioni globali</h3>
            <p className="text-xs text-gray-400">Stato delle integrazioni clienti</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl bg-gray-50 p-4">
            <p className="text-[11px] uppercase tracking-wider font-bold text-gray-400">Configurazioni attive</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{status?.stats?.total_configs ?? 0}</p>
          </div>
          <div className="rounded-xl bg-emerald-50 p-4">
            <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">Con almeno 1 utente connesso</p>
            <p className="text-2xl font-black text-emerald-900 mt-1">{status?.stats?.configs_with_connections ?? 0}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={fetchStatus} variant="outline" className="rounded-xl text-xs gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna stato
        </Button>
      </div>
    </div>
  );
};

export default TelegramCentralPanel;
